import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (!isHome) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const overHero = !scrolled && isHome;
  const headerBg = overHero
    ? "bg-transparent"
    : "bg-[#F9F8F6]/95 backdrop-blur-md border-b border-[#1A1B1A]/10";

  const textColor = overHero ? "text-white" : "text-[#1A1B1A]";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2">
          <img
            src={overHero ? "/assets/sustrai-logo-light.png" : "/assets/sustrai-logo.png"}
            alt="Sustrai Aventura"
            className="h-12 md:h-14 w-auto transition-opacity duration-300"
          />
        </Link>

        <nav className={`hidden lg:flex items-center gap-10 ${textColor}`}>
          <button data-testid={NAV.linkInicio} onClick={() => goSection("hero")} className="rv-nav-link text-sm font-semibold uppercase tracking-wider">
            {t("nav.inicio")}
          </button>
          <button data-testid={NAV.linkActividades} onClick={() => goSection("actividades")} className="rv-nav-link text-sm font-semibold uppercase tracking-wider">
            {t("nav.actividades")}
          </button>
          <button data-testid={NAV.linkSobre} onClick={() => goSection("sobre")} className="rv-nav-link text-sm font-semibold uppercase tracking-wider">
            {t("nav.sobre")}
          </button>
          <button data-testid={NAV.linkContacto} onClick={() => goSection("contacto")} className="rv-nav-link text-sm font-semibold uppercase tracking-wider">
            {t("nav.contacto")}
          </button>
        </nav>

        <div className={`hidden lg:flex items-center gap-4 ${textColor}`}>
          <button
            data-testid={NAV.langToggle}
            onClick={toggle}
            className="text-xs font-bold tracking-[0.2em] uppercase border-2 border-current px-3 py-2 hover:bg-[#F26419] hover:text-white hover:border-[#F26419] transition-colors"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
          <button
            data-testid={NAV.reservarBtn}
            onClick={() => goSection("reservar")}
            className="bg-[#F26419] hover:bg-[#D95512] text-white font-bold tracking-[0.1em] uppercase px-6 py-3 text-sm transition-colors"
          >
            {t("nav.reservar")}
          </button>
        </div>

        <button
          data-testid={NAV.mobileMenuBtn}
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${textColor}`}
          aria-label="menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#1A1B1A]/10">
          <div className="px-6 py-6 flex flex-col gap-4 text-[#1A1B1A]">
            <button onClick={() => goSection("hero")} className="text-left text-sm font-semibold uppercase tracking-wider">{t("nav.inicio")}</button>
            <button onClick={() => goSection("actividades")} className="text-left text-sm font-semibold uppercase tracking-wider">{t("nav.actividades")}</button>
            <button onClick={() => goSection("sobre")} className="text-left text-sm font-semibold uppercase tracking-wider">{t("nav.sobre")}</button>
            <button onClick={() => goSection("contacto")} className="text-left text-sm font-semibold uppercase tracking-wider">{t("nav.contacto")}</button>
            <div className="flex gap-3 pt-2">
              <button onClick={toggle} className="text-xs font-bold tracking-[0.2em] uppercase border-2 border-[#1A1B1A] px-3 py-2">
                {lang === "es" ? "EN" : "ES"}
              </button>
              <button onClick={() => goSection("reservar")} className="flex-1 bg-[#F26419] text-white font-bold tracking-[0.1em] uppercase px-4 py-3 text-sm">
                {t("nav.reservar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
